import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  HeartIcon,
  BellIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  TrophyIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { useWallet } from '../../context/WalletContext';

// Mock data
const recentActivity = [
  { type: 'bid', item: 'CryptoPunk #1234', amount: '5.5 ETH', time: '2 hours ago', status: 'active' },
  { type: 'outbid', item: 'Bored Ape #5678', amount: '12 ETH', time: '5 hours ago', status: 'outbid' },
  { type: 'won', item: 'Azuki #9012', amount: '8.2 ETH', time: '1 day ago', status: 'won' },
  { type: 'watchlist', item: 'Doodle #3456', amount: '3.5 ETH', time: '2 days ago', status: 'watching' },
];

const activeBids = [
  { id: 1, name: 'Equus Dualis II #7', image: '/Equus Dualis/Equus Dualis II ) 7.png', myBid: 5.5, currentBid: 5.5, endsIn: '2h 15m' },
  { id: 2, name: 'Warrior Mythology #12', image: '/warrior mythology/warrior mythology12.jpg', myBid: 10, currentBid: 12, endsIn: '45m' },
];

const watchlistItems = [
  { id: 1, name: 'Equus Dualis II #14', image: '/Equus Dualis/Equus Dualis II ) 14.png', currentBid: 4.8, endsIn: '3h' },
  { id: 2, name: 'Warrior Mythology #28', image: '/warrior mythology/warrior mythology28.jpg', currentBid: 2.1, endsIn: '6h' },
];

const DashboardHome: React.FC = () => {
  const { address, balance, ndhBalance, tier } = useWallet();

  const getTierColor = (t: string) => {
    switch (t) {
      case 'PLATINUM': return 'from-slate-300 to-indigo-400';
      case 'GOLD': return 'from-yellow-400 to-amber-500';
      case 'SILVER': return 'from-slate-400 to-slate-300';
      case 'BRONZE': return 'from-amber-700 to-amber-600';
      default: return 'from-dark-400 to-dark-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-400/10';
      case 'outbid': return 'text-red-400 bg-red-400/10';
      case 'won': return 'text-primary-400 bg-primary-400/10';
      default: return 'text-gray-400 bg-gray-400/10';
    }
  };

  const stats = [
    { label: 'Pujas Activas', value: '3', icon: ChartBarIcon, href: '/dashboard/bids', color: 'text-blue-400' },
    { label: 'Items Ganados', value: '12', icon: TrophyIcon, href: '/dashboard/purchases', color: 'text-green-400' },
    { label: 'En Watchlist', value: '8', icon: HeartIcon, href: '/dashboard/watchlist', color: 'text-red-400' },
    { label: 'NDH Stakeado', value: parseFloat(ndhBalance).toLocaleString(), icon: CurrencyDollarIcon, href: '/dashboard/staking', color: 'text-primary-400' },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
        <p className="text-gray-400">Bienvenido de vuelta. Aquí tienes un resumen de tu actividad.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Link
                to={stat.href}
                className="block bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-primary-500/50 transition-all"
              >
                <Icon className={`w-8 h-8 ${stat.color} mb-3`} />
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-gray-400 text-sm">{stat.label}</p>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Bids */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700">
            <div className="p-4 border-b border-dark-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <ChartBarIcon className="w-5 h-5 text-blue-400" />
                Mis Pujas Activas
              </h2>
              <Link to="/dashboard/bids" className="text-primary-400 text-sm hover:text-primary-300">
                Ver todas →
              </Link>
            </div>
            <div className="p-4">
              {activeBids.length > 0 ? (
                <div className="space-y-3">
                  {activeBids.map((bid) => (
                    <div
                      key={bid.id}
                      className="flex items-center justify-between p-3 bg-dark-900/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={bid.image}
                          alt={bid.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div>
                          <p className="text-white font-medium">{bid.name}</p>
                          <p className="text-gray-400 text-sm">Tu puja: {bid.myBid} ETH</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-medium ${
                          bid.myBid >= bid.currentBid ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {bid.myBid >= bid.currentBid ? 'Ganando' : 'Superado'}
                        </p>
                        <p className="text-gray-500 text-sm flex items-center gap-1">
                          <ClockIcon className="w-3 h-3" />
                          {bid.endsIn}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No tienes pujas activas</p>
              )}
            </div>
          </div>

          {/* Watchlist */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700">
            <div className="p-4 border-b border-dark-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <HeartIcon className="w-5 h-5 text-red-400" />
                Mis Intereses
              </h2>
              <Link to="/dashboard/watchlist" className="text-primary-400 text-sm hover:text-primary-300">
                Ver todos →
              </Link>
            </div>
            <div className="p-4">
              {watchlistItems.length > 0 ? (
                <div className="space-y-3">
                  {watchlistItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-dark-900/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <div>
                          <p className="text-white font-medium">{item.name}</p>
                          <p className="text-gray-400 text-sm">Puja actual: {item.currentBid} ETH</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-500 text-sm flex items-center gap-1">
                          <ClockIcon className="w-3 h-3" />
                          Termina en {item.endsIn}
                        </p>
                        <Link
                          to={`/auction/${item.id}`}
                          className="text-primary-400 text-sm hover:text-primary-300"
                        >
                          Ver subasta
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Tu watchlist está vacía</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Wallet Info */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Mi Wallet</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Balance ETH</span>
                <span className="text-white font-medium">{parseFloat(balance).toFixed(4)} ETH</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Balance NDH</span>
                <span className="text-primary-400 font-medium">{parseFloat(ndhBalance).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Tier</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-r ${getTierColor(tier)} text-white`}>
                  {tier || 'Básico'}
                </span>
              </div>
            </div>
            <Link
              to="/dashboard/staking"
              className="mt-4 block w-full py-2 bg-primary-500/20 text-primary-400 text-center rounded-lg hover:bg-primary-500/30 transition-colors text-sm"
            >
              Gestionar Staking
            </Link>
          </div>

          {/* Recent Activity */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700">
            <div className="p-4 border-b border-dark-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <BellIcon className="w-5 h-5 text-primary-400" />
                Actividad Reciente
              </h3>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                {recentActivity.map((activity, idx) => (
                  <div key={idx} className="flex items-start gap-3 py-2 border-b border-dark-700 last:border-0">
                    <div className={`p-1.5 rounded-lg ${getStatusColor(activity.status)}`}>
                      {activity.type === 'bid' && <ArrowTrendingUpIcon className="w-4 h-4" />}
                      {activity.type === 'outbid' && <ArrowTrendingUpIcon className="w-4 h-4 rotate-180" />}
                      {activity.type === 'won' && <TrophyIcon className="w-4 h-4" />}
                      {activity.type === 'watchlist' && <EyeIcon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-white text-sm">{activity.item}</p>
                      <p className="text-gray-500 text-xs">{activity.amount} • {activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-dark-800/50 rounded-xl border border-dark-700 p-4">
            <h3 className="text-sm font-semibold text-gray-400 mb-3">Acciones Rápidas</h3>
            <div className="space-y-2">
              <Link
                to="/dashboard/create"
                className="block w-full py-2 bg-primary-500 hover:bg-primary-600 text-white text-center rounded-lg transition-colors text-sm"
              >
                Crear Nueva Subasta
              </Link>
              <Link
                to="/explore"
                className="block w-full py-2 bg-dark-700 hover:bg-dark-600 text-white text-center rounded-lg transition-colors text-sm"
              >
                Explorar Subastas
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
